/**
 * Keeps a twin-chat visit recoverable when the renderer dies.
 *
 * Chrome "Aw, Snap" (and other renderer kills) often restore the previous
 * session-history entry. Results → `/chats/:token` therefore comes back as
 * Results, and `location.state.backHref` is gone with it. An in-memory React
 * tree cannot run during that kill, so the chat visit is armed in
 * sessionStorage before navigation. A clean leave disarms it, including a
 * later route change after an error boundary already unmounted the chat, and
 * a full-document departure (`pagehide`). A renderer kill does not fire
 * `pagehide`, so that record stays armed. The next app load redirects back
 * to the chat URL only when the record is still armed.
 *
 * Cache scope: not an LLM prompt.
 */

import { track, AnalyticsEvents } from '@/lib/services/analytics'

const STORAGE_KEY = 'dt.chat.crashRestore.v1'

/** Crash recovery is for the bounce that happens immediately, not a later visit. */
const MAX_AGE_MS = 3 * 60 * 1000

/** Stop redirecting if the chat URL itself keeps dying. */
const MAX_RECOVERIES = 2

export type ChatCrashSource = 'react' | 'window' | 'unhandledrejection' | 'history_restore'

export interface ChatCrashRestoreRecord {
  href: string
  roomToken: string
  studyId: string | null
  backHref: string | null
  armed: boolean
  armedAt: number
  recoveries: number
  sendStarted: boolean
}

export type ChatCrashRecovery =
  | {
      action: 'restore'
      href: string
      roomToken: string
      studyId: string | null
      sendStarted: boolean
    }
  | {
      action: 'exhausted'
      roomToken: string
      studyId: string | null
      sendStarted: boolean
    }

let pendingSendStarted = false
let lastBeaconKey = ''
let lastBeaconAt = 0
let recentRecovery: { pathname: string; at: number; result: ChatCrashRecovery } | null = null
/**
 * Held only in this document when `pagehide` parks it in the back/forward
 * cache. The next document has a fresh heap, so it cannot see this copy.
 */
let suspendedRecord: ChatCrashRestoreRecord | null = null

/** Test hook. Production reloads reset module state with the document. */
export function resetChatCrashRestoreRuntime(): void {
  pendingSendStarted = false
  lastBeaconKey = ''
  lastBeaconAt = 0
  recentRecovery = null
  suspendedRecord = null
}

function storage(): Storage | null {
  try {
    if (typeof sessionStorage === 'undefined') return null
    return sessionStorage
  } catch {
    return null
  }
}

export function pathnameOf(href: string): string {
  try {
    return new URL(href, 'http://localhost').pathname
  } catch {
    return href.split('?')[0] ?? href
  }
}

export function readChatCrashRestore(): ChatCrashRestoreRecord | null {
  const store = storage()
  if (!store) return null
  const raw = store.getItem(STORAGE_KEY)
  if (!raw) return null
  try {
    const parsed = JSON.parse(raw) as Partial<ChatCrashRestoreRecord>
    if (!parsed || typeof parsed.href !== 'string' || typeof parsed.roomToken !== 'string') {
      return null
    }
    return {
      href: parsed.href,
      roomToken: parsed.roomToken,
      studyId: typeof parsed.studyId === 'string' ? parsed.studyId : null,
      backHref: typeof parsed.backHref === 'string' ? parsed.backHref : null,
      armed: parsed.armed === true,
      armedAt: typeof parsed.armedAt === 'number' ? parsed.armedAt : 0,
      recoveries: typeof parsed.recoveries === 'number' ? parsed.recoveries : 0,
      sendStarted: parsed.sendStarted === true,
    }
  } catch {
    return null
  }
}

function writeChatCrashRestore(record: ChatCrashRestoreRecord): void {
  const store = storage()
  if (!store) return
  try {
    store.setItem(STORAGE_KEY, JSON.stringify(record))
  } catch {
    // sessionStorage can throw in private mode; the visit still works.
  }
}

export function armChatCrashRestore(input: {
  href: string
  roomToken: string
  studyId?: string | null
  backHref?: string | null
  now?: number
}): void {
  const existing = readChatCrashRestore()
  const sameRoom = existing?.roomToken === input.roomToken
  const sendStarted = Boolean((sameRoom && existing?.sendStarted) || pendingSendStarted)
  writeChatCrashRestore({
    href: input.href,
    roomToken: input.roomToken,
    studyId: input.studyId ?? (sameRoom ? existing?.studyId ?? null : null),
    backHref:
      input.backHref
      ?? (sameRoom ? existing?.backHref ?? null : null),
    armed: true,
    armedAt: input.now ?? Date.now(),
    recoveries: sameRoom ? existing?.recoveries ?? 0 : 0,
    sendStarted,
  })
}

export function disarmChatCrashRestore(): void {
  pendingSendStarted = false
  recentRecovery = null
  suspendedRecord = null
  const store = storage()
  if (!store) return
  try {
    store.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}

function clearChatCrashRestoreStorage(): void {
  const store = storage()
  if (!store) return
  try {
    store.removeItem(STORAGE_KEY)
  } catch {
    // ignore
  }
}

/**
 * Clean document departure.
 *
 * A renderer kill does not fire `pagehide`, so the session record stays and
 * the next document can restore the chat. A real navigation must clear
 * sessionStorage even when `persisted` is true: Chrome fires that for
 * bfcache, and the newly loaded document would otherwise treat the record
 * as a crash. The same document resumes the record from memory on
 * `pageshow` only if it is still on the chat URL.
 */
export function noteChatDocumentHidden(persisted: boolean): void {
  if (!persisted) {
    disarmChatCrashRestore()
    return
  }
  suspendedRecord = readChatCrashRestore()
  clearChatCrashRestoreStorage()
}

/** Puts a bfcache-suspended record back when this same document returns. */
export function noteChatDocumentShown(
  persisted: boolean,
  pathname: string,
  now = Date.now(),
): void {
  if (!persisted) return
  const record = suspendedRecord
  suspendedRecord = null
  if (!record?.armed) return
  if (pathnameOf(record.href) !== pathname) return
  writeChatCrashRestore({ ...record, armedAt: now })
}

/**
 * Disarm only when the document has actually left the armed chat URL.
 * A React error boundary unmounts ChatPage while the URL stays put; that
 * must not clear the record.
 */
export function disarmChatCrashRestoreIfLeft(pathname: string): void {
  const record = readChatCrashRestore()
  if (!record) return
  if (pathnameOf(record.href) === pathname) return
  disarmChatCrashRestore()
}

export function noteChatCrashSendStarted(): void {
  pendingSendStarted = true
  const record = readChatCrashRestore()
  if (!record?.armed || record.sendStarted) return
  writeChatCrashRestore({ ...record, sendStarted: true })
}

export function readChatBackHref(roomToken: string | null | undefined): string | null {
  if (!roomToken) return null
  const record = readChatCrashRestore()
  if (!record || record.roomToken !== roomToken) return null
  return record.backHref
}

export function consumeChatCrashRecovery(
  pathname: string,
  now = Date.now(),
): ChatCrashRecovery | null {
  if (
    recentRecovery
    && recentRecovery.pathname === pathname
    && now - recentRecovery.at < 1000
  ) {
    return recentRecovery.result
  }

  const record = readChatCrashRestore()
  if (!record?.armed) return null
  if (pathnameOf(record.href) === pathname) return null
  if (now - record.armedAt > MAX_AGE_MS) {
    disarmChatCrashRestore()
    return null
  }

  if (record.recoveries >= MAX_RECOVERIES) {
    const exhausted: ChatCrashRecovery = {
      action: 'exhausted',
      roomToken: record.roomToken,
      studyId: record.studyId,
      sendStarted: record.sendStarted,
    }
    disarmChatCrashRestore()
    recentRecovery = { pathname, at: now, result: exhausted }
    return exhausted
  }

  writeChatCrashRestore({ ...record, recoveries: record.recoveries + 1 })
  const restore: ChatCrashRecovery = {
    action: 'restore',
    href: record.href,
    roomToken: record.roomToken,
    studyId: record.studyId,
    sendStarted: record.sendStarted,
  }
  recentRecovery = { pathname, at: now, result: restore }
  return restore
}

export function reportChatPageCrash(details: {
  source: ChatCrashSource
  roomToken: string | null
  studyId: string | null
  sendStarted: boolean
}): void {
  const beaconKey = `${details.source}|${details.roomToken ?? ''}|${details.sendStarted}`
  const now = Date.now()
  if (beaconKey === lastBeaconKey && now - lastBeaconAt < 1000) return
  lastBeaconKey = beaconKey
  lastBeaconAt = now

  const properties: Record<string, string | boolean> = {
    crash_source: details.source,
    send_started: details.sendStarted,
  }
  if (details.roomToken) properties.room_id = details.roomToken
  if (details.studyId) properties.study_id = details.studyId

  // Mixpanel init runs in a parent effect. Reporting from a child effect in
  // the same commit would otherwise drop the event.
  queueMicrotask(() => {
    track(AnalyticsEvents.CHAT_PAGE_CRASH, properties)
  })
}
